import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface TempleReportData {
  donations: {
    total: number;
    monthly: number;
    daily: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
    trends: Array<{ date: string; amount: number; count: number }>;
  };
  bookings: {
    total: number;
    monthly: number;
    daily: number;
    byStatus: Record<string, number>;
    byPooja: Record<string, number>;
    trends: Array<{ date: string; count: number; revenue: number }>;
  };
  wallet: {
    currentBalance: number;
    monthlyCredits: number;
    monthlyDebits: number;
    transactions: Array<{ date: string; type: string; amount: number; description: string }>;
  };
  summary: {
    totalRevenue: number;
    monthlyRevenue: number;
    activeBookings: number;
    pendingDonations: number;
    topPoojas: Array<{ name: string; bookings: number; revenue: number }>;
    topDonors: Array<{ name: string; amount: number; count: number }>;
  };
}

interface DateRange {
  start: string;
  end: string;
  preset: 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';
}

const TempleReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [reportData, setReportData] = useState<TempleReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    preset: 'month'
  });

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [donationsRes, bookingsRes, walletRes] = await Promise.all([
        adminApiClient.getDonations({
          templeId: user?.templeId,
          startDate: dateRange.start,
          endDate: dateRange.end
        }),
        adminApiClient.getBookingsByTemple(user?.templeId || '', {
          page: 1,
          limit: 1000
        }),
        adminApiClient.getWalletBalance(user?.templeId || '')
      ]);

      // Process donations data
      const donations = donationsRes.success ? donationsRes.data?.donations || [] : [];
      const donationsData = {
        total: donations.reduce((sum: number, d: any) => sum + d.amount, 0),
        monthly: donations
          .filter((d: any) => {
            const donationDate = new Date(d.createdAt);
            const currentMonth = new Date().getMonth();
            const currentYear = new Date().getFullYear();
            return donationDate.getMonth() === currentMonth && donationDate.getFullYear() === currentYear;
          })
          .reduce((sum: number, d: any) => sum + d.amount, 0),
        daily: donations
          .filter((d: any) => {
            const donationDate = new Date(d.createdAt).toDateString();
            const today = new Date().toDateString();
            return donationDate === today;
          })
          .reduce((sum: number, d: any) => sum + d.amount, 0),
        byType: donations.reduce((acc: Record<string, number>, d: any) => {
          acc[d.type] = (acc[d.type] || 0) + d.amount;
          return acc;
        }, {}),
        byStatus: donations.reduce((acc: Record<string, number>, d: any) => {
          acc[d.status] = (acc[d.status] || 0) + d.amount;
          return acc;
        }, {}),
        trends: donations.reduce((acc: Record<string, { amount: number; count: number }>, d: any) => {
          const date = new Date(d.createdAt).toISOString().split('T')[0];
          if (!acc[date]) {
            acc[date] = { amount: 0, count: 0 };
          }
          acc[date].amount += d.amount;
          acc[date].count += 1;
          return acc;
        }, {})
      };

      // Process bookings data
      const bookings = bookingsRes.success ? bookingsRes.data?.bookings || [] : [];
      const bookingsData = {
        total: bookings.length,
        monthly: bookings.filter((b: any) => {
          const bookingDate = new Date(b.createdAt);
          const currentMonth = new Date().getMonth();
          const currentYear = new Date().getFullYear();
          return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
        }).length,
        daily: bookings.filter((b: any) => {
          const bookingDate = new Date(b.createdAt).toDateString();
          const today = new Date().toDateString();
          return bookingDate === today;
        }).length,
        byStatus: bookings.reduce((acc: Record<string, number>, b: any) => {
          acc[b.status] = (acc[b.status] || 0) + 1;
          return acc;
        }, {}),
        byPooja: bookings.reduce((acc: Record<string, number>, b: any) => {
          const poojaName = b.pooja?.name || 'Unknown';
          acc[poojaName] = (acc[poojaName] || 0) + 1;
          return acc;
        }, {}),
        trends: bookings.reduce((acc: Record<string, { count: number; revenue: number }>, b: any) => {
          const date = new Date(b.createdAt).toISOString().split('T')[0];
          if (!acc[date]) {
            acc[date] = { count: 0, revenue: 0 };
          }
          acc[date].count += 1;
          acc[date].revenue += b.amount || 0;
          return acc;
        }, {})
      };

      // Process wallet data
      const wallet = walletRes.success ? walletRes.data?.wallet : null;
      const walletData = {
        currentBalance: wallet?.balance || 0,
        monthlyCredits: 0, // Would need wallet transactions API
        monthlyDebits: 0, // Would need wallet transactions API
        transactions: [] // Would need wallet transactions API
      };

      // Generate summary data
      const summary = {
        totalRevenue: donationsData.total + bookings.reduce((sum: number, b: any) => sum + (b.amount || 0), 0),
        monthlyRevenue: donationsData.monthly + bookingsData.monthly,
        activeBookings: bookings.filter((b: any) => b.status === 'confirmed' || b.status === 'pending').length,
        pendingDonations: donations.filter((d: any) => d.status === 'pending').length,
        topPoojas: Object.entries(bookingsData.byPooja)
          .map(([name, count]) => ({
            name,
            bookings: count,
            revenue: bookings
              .filter((b: any) => (b.pooja?.name || 'Unknown') === name)
              .reduce((sum: number, b: any) => sum + (b.amount || 0), 0)
          }))
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5),
        topDonors: Object.entries(
          donations.reduce((acc: Record<string, { amount: number; count: number }>, d: any) => {
            const donorName = d.donorName || d.donor?.name || 'Anonymous';
            if (!acc[donorName]) {
              acc[donorName] = { amount: 0, count: 0 };
            }
            acc[donorName].amount += d.amount;
            acc[donorName].count += 1;
            return acc;
          }, {})
        )
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5)
      };

      setReportData({
        donations: {
          ...donationsData,
          trends: Object.entries(donationsData.trends).map(([date, data]) => ({ date, ...data }))
        },
        bookings: {
          ...bookingsData,
          trends: Object.entries(bookingsData.trends).map(([date, data]) => ({ date, ...data }))
        },
        wallet: walletData,
        summary
      });

    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [user?.templeId, dateRange]);

  useEffect(() => {
    if (user?.templeId) {
      fetchReportData();
    }
  }, [user?.templeId, fetchReportData]);

  const handleDateRangeChange = (preset: string) => {
    const now = new Date();
    let start: Date, end: Date = now;

    switch (preset) {
      case 'today':
        start = end = now;
        break;
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterStart, 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      preset: preset as any
    });
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <p>Loading temple reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-banner">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
        <button onClick={() => setError(null)} className="error-close">×</button>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Report Data Available</h3>
          <p>Unable to generate reports for your temple.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="header-left">
          <h2>Temple Reports & Analytics</h2>
          <p>Comprehensive insights into your temple's performance</p>
        </div>
        <div className="header-actions">
          <div className="date-range-selector">
            <label>Date Range:</label>
            <select
              value={dateRange.preset}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="form-select"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateRange.preset === 'custom' && (
              <>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="form-input"
                />
                <span>to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="form-input"
                />
              </>
            )}
          </div>
          <button onClick={fetchReportData} className="btn-secondary" disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <h3>${reportData.summary.totalRevenue.toLocaleString()}</h3>
            <p>Total Revenue</p>
            <span className="metric-change positive">
              +${reportData.summary.monthlyRevenue.toLocaleString()} this month
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">📅</div>
          <div className="metric-content">
            <h3>{reportData.summary.activeBookings}</h3>
            <p>Active Bookings</p>
            <span className="metric-change">
              {reportData.bookings.total} total bookings
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">🎁</div>
          <div className="metric-content">
            <h3>${reportData.donations.total.toLocaleString()}</h3>
            <p>Total Donations</p>
            <span className="metric-change">
              {reportData.summary.pendingDonations} pending
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">🏦</div>
          <div className="metric-content">
            <h3>${reportData.wallet.currentBalance.toLocaleString()}</h3>
            <p>Wallet Balance</p>
            <span className="metric-change">
              Current available funds
            </span>
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="reports-grid">
        {/* Donations Breakdown */}
        <div className="report-card">
          <div className="report-header">
            <h3>Donations Analysis</h3>
          </div>
          <div className="report-content">
            <div className="report-stats">
              <div className="stat-item">
                <span className="stat-label">Daily:</span>
                <span className="stat-value">${reportData.donations.daily.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Monthly:</span>
                <span className="stat-value">${reportData.donations.monthly.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="breakdown-section">
              <h4>By Type</h4>
              <div className="breakdown-list">
                {Object.entries(reportData.donations.byType).map(([type, amount]) => (
                  <div key={type} className="breakdown-item">
                    <span className="breakdown-label">{type}</span>
                    <span className="breakdown-value">${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="breakdown-section">
              <h4>By Status</h4>
              <div className="breakdown-list">
                {Object.entries(reportData.donations.byStatus).map(([status, amount]) => (
                  <div key={status} className="breakdown-item">
                    <span className="breakdown-label">{status}</span>
                    <span className="breakdown-value">${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bookings Breakdown */}
        <div className="report-card">
          <div className="report-header">
            <h3>Bookings Analysis</h3>
          </div>
          <div className="report-content">
            <div className="report-stats">
              <div className="stat-item">
                <span className="stat-label">Daily:</span>
                <span className="stat-value">{reportData.bookings.daily}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Monthly:</span>
                <span className="stat-value">{reportData.bookings.monthly}</span>
              </div>
            </div>
            
            <div className="breakdown-section">
              <h4>By Status</h4>
              <div className="breakdown-list">
                {Object.entries(reportData.bookings.byStatus).map(([status, count]) => (
                  <div key={status} className="breakdown-item">
                    <span className="breakdown-label">{status}</span>
                    <span className="breakdown-value">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="breakdown-section">
              <h4>Top Poojas</h4>
              <div className="breakdown-list">
                {reportData.summary.topPoojas.map((pooja, index) => (
                  <div key={index} className="breakdown-item">
                    <span className="breakdown-label">{pooja.name}</span>
                    <span className="breakdown-value">{pooja.bookings} bookings</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Top Donors */}
        <div className="report-card">
          <div className="report-header">
            <h3>Top Donors</h3>
          </div>
          <div className="report-content">
            <div className="top-donors-list">
              {reportData.summary.topDonors.map((donor, index) => (
                <div key={index} className="donor-item">
                  <div className="donor-rank">#{index + 1}</div>
                  <div className="donor-info">
                    <div className="donor-name">{donor.name}</div>
                    <div className="donor-stats">
                      ${donor.amount.toLocaleString()} • {donor.count} donations
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Poojas */}
        <div className="report-card">
          <div className="report-header">
            <h3>Popular Poojas</h3>
          </div>
          <div className="report-content">
            <div className="top-poojas-list">
              {reportData.summary.topPoojas.map((pooja, index) => (
                <div key={index} className="pooja-item">
                  <div className="pooja-rank">#{index + 1}</div>
                  <div className="pooja-info">
                    <div className="pooja-name">{pooja.name}</div>
                    <div className="pooja-stats">
                      {pooja.bookings} bookings • ${pooja.revenue.toLocaleString()} revenue
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempleReportsPage;
